import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { getAuthHeaders } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const hdr = () => ({ ...getAuthHeaders(), 'Content-Type': 'application/json' });
const opts = () => ({ headers: hdr(), withCredentials: true });

const EMPTY_COURSE = { title:'',category:'Robotics',description:'',short_description:'',image_url:'',duration:'',level:'Beginner',price:0,features:[],is_published:true,admin_notes:'' };
const EMPTY_PROJECT = { title:'',description:'',image_url:'',bg_color:'bg-p1' };

export default function AdminDashboard() {
  const [stats, setStats] = useState({});
  const [courses, setCourses] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);

  // Dialogs
  const [courseDialog, setCourseDialog] = useState(false);
  const [editCourse, setEditCourse] = useState(null);
  const [courseForm, setCourseForm] = useState(EMPTY_COURSE);
  const [featuresText, setFeaturesText] = useState('');

  const [projectDialog, setProjectDialog] = useState(false);
  const [editProject, setEditProject] = useState(null);
  const [projectForm, setProjectForm] = useState(EMPTY_PROJECT);

  const [msgDialog, setMsgDialog] = useState(false);
  const [msgUser, setMsgUser] = useState(null);
  const [msgContent, setMsgContent] = useState('');
  const [msgType, setMsgType] = useState('general');
  const [chatMsgs, setChatMsgs] = useState([]);

  const [notesDialog, setNotesDialog] = useState(false);
  const [notesEnrollment, setNotesEnrollment] = useState(null);
  const [notesText, setNotesText] = useState('');

  const [saving, setSaving] = useState(false);

  const [addEnrollmentDialog, setAddEnrollmentDialog] = useState(false);
  const [addEnrollmentForm, setAddEnrollmentForm] = useState({ user_id:'', course_id:'', phone:'', message:'' });

  const [addStudentDialog, setAddStudentDialog] = useState(false);
  const [addStudentForm, setAddStudentForm] = useState({ name:'', email:'', phone:'', password:'' });

  const [addInquiryDialog, setAddInquiryDialog] = useState(false);
  const [addInquiryForm, setAddInquiryForm] = useState({ name:'', email:'', phone:'', age:'', message:'' });

  // Settings
  const [settings, setSettings] = useState({ email:'', whatsapp:'', whatsapp_link:'', instagram:'', instagram_link:'', website:'', phone:'', address:'' });
  const [settingsSaved, setSettingsSaved] = useState(false);

  const load = useCallback(async () => {
    try {
      const [s,c,e,ct,p,u,st] = await Promise.all([
        axios.get(`${API}/admin/stats`, opts()), axios.get(`${API}/courses`, opts()),
        axios.get(`${API}/enrollments`, opts()), axios.get(`${API}/contact`, opts()),
        axios.get(`${API}/projects`, opts()), axios.get(`${API}/admin/users`, opts()),
        axios.get(`${API}/settings`, opts()),
      ]);
      setStats(s.data); setCourses(c.data); setEnrollments(e.data);
      setContacts(ct.data); setProjects(p.data); setUsers(u.data);
      setSettings(st.data);
    } catch {}
  }, []);

  useEffect(() => { load(); }, [load]);

  // Course CRUD
  const openNewCourse = () => { setEditCourse(null); setCourseForm(EMPTY_COURSE); setFeaturesText(''); setCourseDialog(true); };
  const openEditCourse = (c) => {
    setEditCourse(c);
    setCourseForm({ title:c.title, category:c.category, description:c.description, short_description:c.short_description||'', image_url:c.image_url||'', duration:c.duration||'', level:c.level||'Beginner', price:c.price||0, features:c.features||[], is_published:c.is_published!==false, admin_notes:c.admin_notes||'' });
    setFeaturesText((c.features||[]).join('\n')); setCourseDialog(true);
  };
  const saveCourse = async (e) => {
    e.preventDefault(); setSaving(true);
    const d = { ...courseForm, features: featuresText.split('\n').filter(f=>f.trim()) };
    try {
      if (editCourse) await axios.put(`${API}/courses/${editCourse.id}`, d, opts());
      else await axios.post(`${API}/courses`, d, opts());
      setCourseDialog(false); load();
    } catch {} setSaving(false);
  };
  const deleteCourse = async (id) => { if (!window.confirm('Delete this course?')) return; try { await axios.delete(`${API}/courses/${id}`, opts()); load(); } catch {} };

  // Project CRUD
  const openNewProject = () => { setEditProject(null); setProjectForm(EMPTY_PROJECT); setProjectDialog(true); };
  const openEditProject = (p) => { setEditProject(p); setProjectForm({ title:p.title, description:p.description, image_url:p.image_url||'', bg_color:p.bg_color||'bg-p1' }); setProjectDialog(true); };
  const saveProject = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      if (editProject) await axios.put(`${API}/projects/${editProject.id}`, projectForm, opts());
      else await axios.post(`${API}/projects`, projectForm, opts());
      setProjectDialog(false); load();
    } catch {} setSaving(false);
  };
  const deleteProject = async (id) => { if (!window.confirm('Delete this project?')) return; try { await axios.delete(`${API}/projects/${id}`, opts()); load(); } catch {} };

  // Enrollment actions
  const updateStatus = async (id, status) => { try { await axios.put(`${API}/enrollments/${id}/status`, { status }, opts()); load(); } catch {} };
  const updatePayment = async (id, ps) => { try { await axios.put(`${API}/enrollments/${id}/payment`, { payment_status: ps }, opts()); load(); } catch {} };
  const openNotes = (enr) => { setNotesEnrollment(enr); setNotesText(enr.admin_notes||''); setNotesDialog(true); };
  const saveNotes = async () => { try { await axios.put(`${API}/enrollments/${notesEnrollment.id}/notes`, { admin_notes: notesText }, opts()); setNotesDialog(false); load(); } catch {} };

  // Messaging
  const openChat = async (u) => {
    setMsgUser(u); setMsgContent(''); setMsgType('general');
    try { const { data } = await axios.get(`${API}/messages/user/${u._id}`, opts()); setChatMsgs(data); } catch { setChatMsgs([]); }
    setMsgDialog(true);
  };
  const sendMsg = async () => {
    if (!msgContent.trim()) return;
    try {
      await axios.post(`${API}/messages`, { user_id: msgUser._id, content: msgContent, msg_type: msgType }, opts());
      setMsgContent('');
      const { data } = await axios.get(`${API}/messages/user/${msgUser._id}`, opts());
      setChatMsgs(data);
    } catch {}
  };

    const markRead = async (id) => { try { await axios.put(`${API}/contact/${id}/read`, {}, opts()); load(); } catch {} };

  const deleteEnrollment = async (id) => {
    if (!window.confirm('Delete this enrollment?')) return;
    try { await axios.delete(`${API}/admin/enrollments/${id}`, opts()); load(); } catch {}
  };

  const createEnrollment = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/enrollments`, addEnrollmentForm, opts());
      setAddEnrollmentDialog(false);
      setAddEnrollmentForm({ user_id:'', course_id:'', phone:'', message:'' });
      load();
    } catch {}
  };

  const deleteStudent = async (userId) => {
    if (!window.confirm('Delete this student? Their enrollments will also be removed.')) return;
    try { await axios.delete(`${API}/admin/users/${userId}`, opts()); load(); } catch {}
  };

  const createStudent = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/users`, addStudentForm, opts());
      setAddStudentDialog(false);
      setAddStudentForm({ name:'', email:'', phone:'', password:'' });
      load();
    } catch {}
  };

  const deleteInquiry = async (id) => {
    if (!window.confirm('Delete this inquiry?')) return;
    try { await axios.delete(`${API}/admin/contact/${id}`, opts()); load(); } catch {}
  };

  const createInquiry = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/contact`, { ...addInquiryForm, country_code: '+91' }, opts());
      setAddInquiryDialog(false);
      setAddInquiryForm({ name:'', email:'', phone:'', age:'', message:'' });
      load();
    } catch {}
  };

  // Settings
  const saveSettings = async () => {
    setSaving(true); setSettingsSaved(false);
    try {
      const { data } = await axios.put(`${API}/settings`, settings, opts());
      setSettings(data); setSettingsSaved(true);
      setTimeout(() => setSettingsSaved(false), 3000);
    } catch {} setSaving(false);
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="admin-dashboard">
      <div className="w-[90%] max-w-[1200px] mx-auto pt-24 pb-16">
        <motion.div initial={{ opacity:0,y:20 }} animate={{ opacity:1,y:0 }} className="mb-8">
          <span className="text-xs font-mono tracking-[0.2em] uppercase text-[#0891B2]">Admin Panel</span>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#1a1a2e] mt-2" style={{ fontFamily:'Outfit' }}>Control Room</h1>
        </motion.div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8" data-testid="admin-stats">
          {[
            { l:'Courses', v:stats.total_courses||0, i:'fa-solid fa-book' },
            { l:'Students', v:stats.total_students||0, i:'fa-solid fa-users' },
            { l:'Enrollments', v:stats.total_enrollments||0, i:'fa-solid fa-chart-bar' },
            { l:'Projects', v:stats.total_projects||0, i:'fa-solid fa-flask' },
            { l:'Inquiries', v:stats.total_contacts||0, i:'fa-solid fa-envelope' },
          ].map(s => (
            <div key={s.l} className="admin-card p-4 flex items-center gap-3" data-testid={`stat-${s.l.toLowerCase()}`}>
              <div className="w-10 h-10 rounded-lg bg-cyan-50 border border-cyan-200 flex items-center justify-center">
                <i className={`${s.i} text-[#0891B2]`} />
              </div>
              <div><div className="text-xl font-bold" style={{ fontFamily:'Outfit' }}>{s.v}</div><div className="text-xs text-gray-500">{s.l}</div></div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <Tabs defaultValue="courses" className="space-y-6" data-testid="admin-tabs">
          <TabsList className="bg-white border border-gray-200 p-1 flex-wrap">
            <TabsTrigger value="courses" className="data-[state=active]:bg-cyan-50 data-[state=active]:text-[#0891B2]" data-testid="tab-courses">Courses</TabsTrigger>
            <TabsTrigger value="projects" className="data-[state=active]:bg-cyan-50 data-[state=active]:text-[#0891B2]" data-testid="tab-projects">Projects</TabsTrigger>
            <TabsTrigger value="enrollments" className="data-[state=active]:bg-cyan-50 data-[state=active]:text-[#0891B2]" data-testid="tab-enrollments">Enrollments</TabsTrigger>
            <TabsTrigger value="users" className="data-[state=active]:bg-cyan-50 data-[state=active]:text-[#0891B2]" data-testid="tab-users">Students</TabsTrigger>
            <TabsTrigger value="inquiries" className="data-[state=active]:bg-cyan-50 data-[state=active]:text-[#0891B2]" data-testid="tab-inquiries">Inquiries</TabsTrigger>
            <TabsTrigger value="settings" className="data-[state=active]:bg-cyan-50 data-[state=active]:text-[#0891B2]" data-testid="tab-settings">Settings</TabsTrigger>
          </TabsList>

          {/* COURSES TAB */}
          <TabsContent value="courses">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-bold" style={{ fontFamily:'Outfit' }}>Manage Courses</h2>
              <button onClick={openNewCourse} className="bb-btn bb-btn-primary !text-sm !py-2 !px-4 !shadow-[3px_3px_0px_rgba(15,23,42,0.9)]" data-testid="add-course-btn"><i className="fa-solid fa-plus mr-2" />Add Course</button>
            </div>
            <div className="admin-card overflow-x-auto">
              <Table>
                <TableHeader><TableRow className="border-gray-100"><TableHead>Title</TableHead><TableHead>Category</TableHead><TableHead>Level</TableHead><TableHead>Duration</TableHead><TableHead>Notes</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
                <TableBody>
                  {courses.map(c => (
                    <TableRow key={c.id} className="border-gray-50" data-testid={`course-row-${c.id}`}>
                      <TableCell className="font-medium">{c.title}</TableCell>
                      <TableCell className="text-gray-500">{c.category}</TableCell>
                      <TableCell className="text-gray-500">{c.level}</TableCell>
                      <TableCell className="text-gray-500">{c.duration}</TableCell>
                      <TableCell className="text-gray-400 text-xs max-w-[150px] truncate">{c.admin_notes || '-'}</TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="sm" onClick={() => openEditCourse(c)} className="text-gray-400 hover:text-[#0891B2]" data-testid={`edit-course-${c.id}`}><i className="fa-solid fa-pen-to-square" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => deleteCourse(c.id)} className="text-gray-400 hover:text-red-500" data-testid={`delete-course-${c.id}`}><i className="fa-solid fa-trash" /></Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* PROJECTS TAB */}
          <TabsContent value="projects">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-bold" style={{ fontFamily:'Outfit' }}>Manage Projects</h2>
              <button onClick={openNewProject} className="bb-btn bb-btn-primary !text-sm !py-2 !px-4 !shadow-[3px_3px_0px_rgba(15,23,42,0.9)]" data-testid="add-project-btn"><i className="fa-solid fa-plus mr-2" />Add Project</button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {projects.map(p => (
                <div key={p.id} className="admin-card overflow-hidden" data-testid={`project-card-${p.id}`}>
                  {p.image_url && <img src={p.image_url} alt={p.title} className="w-full h-32 object-cover" />}
                  <div className="p-4">
                    <h4 className="font-bold text-sm mb-1">{p.title}</h4>
                    <p className="text-xs text-gray-500 mb-3 line-clamp-2">{p.description}</p>
                    <div className="flex gap-2">
                      <Button variant="ghost" size="sm" onClick={() => openEditProject(p)} className="text-gray-400 hover:text-[#0891B2] text-xs" data-testid={`edit-project-${p.id}`}><i className="fa-solid fa-pen mr-1" />Edit</Button>
                      <Button variant="ghost" size="sm" onClick={() => deleteProject(p.id)} className="text-gray-400 hover:text-red-500 text-xs" data-testid={`delete-project-${p.id}`}><i className="fa-solid fa-trash mr-1" />Delete</Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* ENROLLMENTS TAB */}
          <TabsContent value="enrollments">
                        <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold" style={{ fontFamily:'Outfit' }}>All Enrollments</h2>
              <button onClick={() => setAddEnrollmentDialog(true)} className="bb-btn bb-btn-primary !text-sm !py-2 !px-4 !shadow-[3px_3px_0px_rgba(15,23,42,0.9)]" data-testid="add-enrollment-btn"><i className="fa-solid fa-plus mr-2" />Add Enrollment</button>
            </div>
            <div className="admin-card overflow-x-auto">
              <Table>
                <TableHeader><TableRow className="border-gray-100"><TableHead>Student</TableHead><TableHead>Course</TableHead><TableHead>Status</TableHead><TableHead>Payment</TableHead><TableHead>Date</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
                <TableBody>
                  {enrollments.map(e => (
                    <TableRow key={e.id} className="border-gray-50" data-testid={`enrollment-row-${e.id}`}>
                      <TableCell><div className="text-sm font-medium">{e.user_name}</div><div className="text-xs text-gray-400">{e.user_email}</div>{e.user_phone && <div className="text-xs text-gray-400">{e.user_phone}</div>}</TableCell>
                      <TableCell className="text-gray-500 text-sm">{e.course_title}</TableCell>
                      <TableCell>
                        <Select value={e.status} onValueChange={v => updateStatus(e.id, v)}>
                          <SelectTrigger className="w-[110px] h-8 text-xs" data-testid={`enrollment-status-${e.id}`}><SelectValue /></SelectTrigger>
                          <SelectContent className="bg-white"><SelectItem value="pending">Pending</SelectItem><SelectItem value="confirmed">Confirmed</SelectItem><SelectItem value="completed">Completed</SelectItem></SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <button onClick={() => updatePayment(e.id, e.payment_status === 'paid' ? 'unpaid' : 'paid')}
                          className={`text-xs px-3 py-1 rounded-full border font-semibold transition-colors ${
                            e.payment_status === 'paid' ? 'bg-green-50 text-green-700 border-green-200' : 'bg-red-50 text-red-600 border-red-200'
                          }`} data-testid={`payment-toggle-${e.id}`}>
                          {e.payment_status === 'paid' ? <><i className="fa-solid fa-check mr-1" />Paid</> : <><i className="fa-solid fa-xmark mr-1" />Unpaid</>}
                        </button>
                      </TableCell>
                      <TableCell className="text-gray-400 text-xs">{new Date(e.enrolled_at).toLocaleDateString()}</TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="sm" onClick={() => openNotes(e)} className="text-gray-400 hover:text-[#0891B2] text-xs" data-testid={`notes-btn-${e.id}`}><i className="fa-solid fa-sticky-note mr-1" />Notes</Button>
                        <Button variant="ghost" size="sm" onClick={() => deleteEnrollment(e.id)} className="text-gray-400 hover:text-red-500 text-xs" data-testid={`delete-enrollment-${e.id}`}><i className="fa-solid fa-trash mr-1" />Delete</Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {enrollments.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-gray-400 py-8">No enrollments yet</TableCell></TableRow>}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* STUDENTS/USERS TAB */}
          <TabsContent value="users">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold" style={{ fontFamily:'Outfit' }}>Students</h2>
              <button onClick={() => setAddStudentDialog(true)} className="bb-btn bb-btn-primary !text-sm !py-2 !px-4 !shadow-[3px_3px_0px_rgba(15,23,42,0.9)]" data-testid="add-student-btn"><i className="fa-solid fa-plus mr-2" />Add Student</button>
            </div>
            <div className="admin-card overflow-x-auto">
              <Table>
                <TableHeader><TableRow className="border-gray-100"><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Phone</TableHead><TableHead>Joined</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
                <TableBody>
                  {users.map(u => (
                    <TableRow key={u._id} className="border-gray-50" data-testid={`user-row-${u._id}`}>
                      <TableCell className="font-medium">{u.name}</TableCell>
                      <TableCell className="text-gray-500 text-sm">{u.email}</TableCell>
                      <TableCell className="text-gray-500 text-sm">{u.phone || '-'}</TableCell>
                      <TableCell className="text-gray-400 text-xs">{new Date(u.created_at).toLocaleDateString()}</TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="sm" onClick={() => openChat(u)} className="text-[#0891B2] text-xs" data-testid={`msg-btn-${u._id}`}><i className="fa-solid fa-comment mr-1" />Message</Button>
                        <Button variant="ghost" size="sm" onClick={() => deleteStudent(u._id)} className="text-gray-400 hover:text-red-500 text-xs ml-1" data-testid={`delete-student-${u._id}`}><i className="fa-solid fa-trash mr-1" />Delete</Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* INQUIRIES TAB */}
          <TabsContent value="inquiries">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold" style={{ fontFamily:'Outfit' }}>Contact Inquiries</h2>
              <button onClick={() => setAddInquiryDialog(true)} className="bb-btn bb-btn-primary !text-sm !py-2 !px-4 !shadow-[3px_3px_0px_rgba(15,23,42,0.9)]" data-testid="add-inquiry-btn"><i className="fa-solid fa-plus mr-2" />Add Inquiry</button>
            </div>
            <div className="space-y-3">
              {contacts.map(c => (
                <div key={c.id} className={`admin-card p-5 ${!c.is_read ? 'border-[#0891B2]' : ''}`} data-testid={`inquiry-${c.id}`}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-1">
                        <span className="font-medium text-sm">{c.name}</span>
                        {!c.is_read && <span className="w-2 h-2 rounded-full bg-[#0891B2]" />}
                        {c.email_verified && <span className="text-xs text-green-600"><i className="fa-solid fa-circle-check mr-1" />Email Verified</span>}
                      </div>
                      <div className="text-xs text-gray-400 mb-2">{c.email} | {c.phone}{c.age ? ` | Age: ${c.age}` : ''}</div>
                      <p className="text-sm text-gray-600">{c.message}</p>
                      <div className="text-xs text-gray-300 mt-2">{new Date(c.created_at).toLocaleString()}</div>
                    </div>
                    <div className="flex gap-2">
                      {!c.is_read && <Button variant="ghost" size="sm" onClick={() => markRead(c.id)} className="text-gray-400 hover:text-[#0891B2] text-xs" data-testid={`mark-read-${c.id}`}><i className="fa-solid fa-eye mr-1" />Read</Button>}
                      <Button variant="ghost" size="sm" onClick={() => deleteInquiry(c.id)} className="text-gray-400 hover:text-red-500 text-xs" data-testid={`delete-inquiry-${c.id}`}><i className="fa-solid fa-trash mr-1" />Delete</Button>
                    </div>
                  </div>
                </div>
              ))}
              {contacts.length === 0 && <div className="admin-card p-12 text-center text-gray-400">No inquiries yet</div>}
            </div>
          </TabsContent>

          {/* SETTINGS TAB */}
          <TabsContent value="settings">
            <h2 className="text-lg font-bold mb-4" style={{ fontFamily:'Outfit' }}>Site Settings</h2>
            <div className="admin-card p-6">
              <p className="text-sm text-gray-500 mb-6">Update the contact information displayed on the homepage.</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div><label className="text-xs text-gray-500 mb-1 block">Contact Email</label><Input value={settings.email||''} onChange={e => setSettings(p=>({...p,email:e.target.value}))} placeholder="hello@buildbotics.in" className="bg-gray-50 focus:border-[#0891B2]" data-testid="settings-email" /></div>
                <div><label className="text-xs text-gray-500 mb-1 block">Phone</label><Input value={settings.phone||''} onChange={e => setSettings(p=>({...p,phone:e.target.value}))} placeholder="+91 9999999999" className="bg-gray-50 focus:border-[#0891B2]" data-testid="settings-phone" /></div>
                <div><label className="text-xs text-gray-500 mb-1 block">WhatsApp Text</label><Input value={settings.whatsapp||''} onChange={e => setSettings(p=>({...p,whatsapp:e.target.value}))} placeholder="Chat with us anytime" className="bg-gray-50 focus:border-[#0891B2]" data-testid="settings-whatsapp" /></div>
                <div><label className="text-xs text-gray-500 mb-1 block">WhatsApp Link</label><Input value={settings.whatsapp_link||''} onChange={e => setSettings(p=>({...p,whatsapp_link:e.target.value}))} placeholder="https://wa.me/919999999999" className="bg-gray-50 focus:border-[#0891B2]" data-testid="settings-whatsapp-link" /></div>
                <div><label className="text-xs text-gray-500 mb-1 block">Instagram Handle</label><Input value={settings.instagram||''} onChange={e => setSettings(p=>({...p,instagram:e.target.value}))} placeholder="@buildbot.in" className="bg-gray-50 focus:border-[#0891B2]" data-testid="settings-instagram" /></div>
                <div><label className="text-xs text-gray-500 mb-1 block">Instagram Link</label><Input value={settings.instagram_link||''} onChange={e => setSettings(p=>({...p,instagram_link:e.target.value}))} placeholder="https://instagram.com/buildbot.in" className="bg-gray-50 focus:border-[#0891B2]" data-testid="settings-instagram-link" /></div>
                <div><label className="text-xs text-gray-500 mb-1 block">Website URL</label><Input value={settings.website||''} onChange={e => setSettings(p=>({...p,website:e.target.value}))} placeholder="https://www.buildbotics.in" className="bg-gray-50 focus:border-[#0891B2]" data-testid="settings-website" /></div>
                <div><label className="text-xs text-gray-500 mb-1 block">Address</label><Input value={settings.address||''} onChange={e => setSettings(p=>({...p,address:e.target.value}))} placeholder="City, State, India" className="bg-gray-50 focus:border-[#0891B2]" data-testid="settings-address" /></div>
              </div>
              <div className="flex items-center gap-4 mt-6">
                <button onClick={saveSettings} disabled={saving} className="bb-btn bb-btn-primary !text-sm !py-2 !px-6 !shadow-[3px_3px_0px_rgba(15,23,42,0.9)]" data-testid="save-settings-btn">
                  {saving ? 'Saving...' : <><i className="fa-solid fa-floppy-disk mr-2" />Save Settings</>}
                </button>
                {settingsSaved && <span className="text-green-600 text-sm" data-testid="settings-saved-msg"><i className="fa-solid fa-check mr-1" />Settings saved!</span>}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Course Dialog */}
      <Dialog open={courseDialog} onOpenChange={setCourseDialog}>
        <DialogContent className="bg-white border-gray-200 max-w-lg max-h-[85vh] overflow-y-auto" data-testid="course-dialog">
          <DialogHeader><DialogTitle style={{ fontFamily:'Outfit' }}>{editCourse ? 'Edit Course' : 'Add New Course'}</DialogTitle></DialogHeader>
          <form onSubmit={saveCourse} className="space-y-3 mt-2">
            <div><label className="text-xs text-gray-500 mb-1 block">Title *</label><Input value={courseForm.title} onChange={e => setCourseForm(p=>({...p,title:e.target.value}))} required className="bg-gray-50 border-gray-200 focus:border-[#0891B2]" data-testid="course-title-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs text-gray-500 mb-1 block">Category</label>
                <Select value={courseForm.category} onValueChange={v => setCourseForm(p=>({...p,category:v}))}>
                  <SelectTrigger className="bg-gray-50" data-testid="course-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white"><SelectItem value="Robotics">Robotics</SelectItem><SelectItem value="Coding">Coding</SelectItem><SelectItem value="Day-to-day Skills">Day-to-day Skills</SelectItem></SelectContent>
                </Select></div>
              <div><label className="text-xs text-gray-500 mb-1 block">Level</label>
                <Select value={courseForm.level} onValueChange={v => setCourseForm(p=>({...p,level:v}))}>
                  <SelectTrigger className="bg-gray-50" data-testid="course-level-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white"><SelectItem value="Beginner">Beginner</SelectItem><SelectItem value="Intermediate">Intermediate</SelectItem><SelectItem value="Advanced">Advanced</SelectItem><SelectItem value="Beginner to Advanced">All Levels</SelectItem></SelectContent>
                </Select></div>
            </div>
            <div><label className="text-xs text-gray-500 mb-1 block">Short Description</label><Input value={courseForm.short_description} onChange={e => setCourseForm(p=>({...p,short_description:e.target.value}))} className="bg-gray-50 focus:border-[#0891B2]" data-testid="course-short-desc-input" /></div>
            <div><label className="text-xs text-gray-500 mb-1 block">Full Description *</label><Textarea value={courseForm.description} onChange={e => setCourseForm(p=>({...p,description:e.target.value}))} required rows={3} className="bg-gray-50 focus:border-[#0891B2] resize-none" data-testid="course-desc-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs text-gray-500 mb-1 block">Duration</label><Input value={courseForm.duration} onChange={e => setCourseForm(p=>({...p,duration:e.target.value}))} placeholder="8 Weeks" className="bg-gray-50 focus:border-[#0891B2]" data-testid="course-duration-input" /></div>
              <div><label className="text-xs text-gray-500 mb-1 block">Image URL</label><Input value={courseForm.image_url} onChange={e => setCourseForm(p=>({...p,image_url:e.target.value}))} className="bg-gray-50 focus:border-[#0891B2]" data-testid="course-image-input" /></div>
            </div>
            <div><label className="text-xs text-gray-500 mb-1 block">Features (one per line)</label><Textarea value={featuresText} onChange={e => setFeaturesText(e.target.value)} rows={2} className="bg-gray-50 focus:border-[#0891B2] resize-none" data-testid="course-features-input" /></div>
            <div><label className="text-xs text-gray-500 mb-1 block">Admin Notes (private)</label><Textarea value={courseForm.admin_notes} onChange={e => setCourseForm(p=>({...p,admin_notes:e.target.value}))} rows={2} placeholder="Internal notes..." className="bg-gray-50 focus:border-[#0891B2] resize-none" data-testid="course-notes-input" /></div>
            <div className="flex gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => setCourseDialog(false)} className="flex-1">Cancel</Button>
              <button type="submit" disabled={saving} className="bb-btn bb-btn-primary flex-1 !text-sm !py-2 !rounded-lg" data-testid="save-course-btn">{saving ? 'Saving...' : editCourse ? 'Update' : 'Create'}</button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Project Dialog */}
      <Dialog open={projectDialog} onOpenChange={setProjectDialog}>
        <DialogContent className="bg-white border-gray-200 max-w-md" data-testid="project-dialog">
          <DialogHeader><DialogTitle style={{ fontFamily:'Outfit' }}>{editProject ? 'Edit Project' : 'Add New Project'}</DialogTitle></DialogHeader>
          <form onSubmit={saveProject} className="space-y-3 mt-2">
            <div><label className="text-xs text-gray-500 mb-1 block">Title *</label><Input value={projectForm.title} onChange={e => setProjectForm(p=>({...p,title:e.target.value}))} required className="bg-gray-50 focus:border-[#0891B2]" data-testid="project-title-input" /></div>
            <div><label className="text-xs text-gray-500 mb-1 block">Description *</label><Textarea value={projectForm.description} onChange={e => setProjectForm(p=>({...p,description:e.target.value}))} required rows={3} className="bg-gray-50 focus:border-[#0891B2] resize-none" data-testid="project-desc-input" /></div>
            <div><label className="text-xs text-gray-500 mb-1 block">Image URL</label><Input value={projectForm.image_url} onChange={e => setProjectForm(p=>({...p,image_url:e.target.value}))} className="bg-gray-50 focus:border-[#0891B2]" data-testid="project-image-input" /></div>
            <div><label className="text-xs text-gray-500 mb-1 block">Background Color</label>
              <Select value={projectForm.bg_color} onValueChange={v => setProjectForm(p=>({...p,bg_color:v}))}>
                <SelectTrigger className="bg-gray-50"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white"><SelectItem value="bg-p1">Teal</SelectItem><SelectItem value="bg-p2">Cyan</SelectItem><SelectItem value="bg-p3">Blue</SelectItem><SelectItem value="bg-p4">Purple</SelectItem></SelectContent>
              </Select></div>
            <div className="flex gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => setProjectDialog(false)} className="flex-1">Cancel</Button>
              <button type="submit" disabled={saving} className="bb-btn bb-btn-primary flex-1 !text-sm !py-2 !rounded-lg" data-testid="save-project-btn">{saving ? 'Saving...' : editProject ? 'Update' : 'Create'}</button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Notes Dialog */}
      <Dialog open={notesDialog} onOpenChange={setNotesDialog}>
        <DialogContent className="bg-white border-gray-200 max-w-md" data-testid="notes-dialog">
          <DialogHeader><DialogTitle style={{ fontFamily:'Outfit' }}>Enrollment Notes</DialogTitle></DialogHeader>
          {notesEnrollment && (
            <div className="space-y-3 mt-2">
              <div className="text-sm"><strong>{notesEnrollment.user_name}</strong> - {notesEnrollment.course_title}</div>
              <Textarea value={notesText} onChange={e => setNotesText(e.target.value)} rows={4} placeholder="Add notes about this enrollment..." className="bg-gray-50 focus:border-[#0891B2] resize-none" data-testid="notes-textarea" />
              <button onClick={saveNotes} className="bb-btn bb-btn-primary bb-btn-block !text-sm !py-2 !rounded-lg" data-testid="save-notes-btn">Save Notes</button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Message Dialog */}
      <Dialog open={msgDialog} onOpenChange={setMsgDialog}>
        <DialogContent className="bg-white border-gray-200 max-w-lg max-h-[80vh] flex flex-col" data-testid="msg-dialog">
          <DialogHeader><DialogTitle style={{ fontFamily:'Outfit' }}>Chat with {msgUser?.name}</DialogTitle></DialogHeader>
          <div className="flex-1 overflow-y-auto space-y-3 py-4 min-h-[200px] max-h-[300px]">
            {chatMsgs.length === 0 && <p className="text-gray-400 text-sm text-center py-8">No messages yet. Send the first one!</p>}
            {chatMsgs.map(m => (
              <div key={m.id} className={`flex ${m.sender_role === 'admin' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[80%] px-4 py-2 rounded-2xl text-sm ${
                  m.sender_role === 'admin' ? 'bg-[#0891B2] text-white rounded-br-sm' : 'bg-gray-100 text-gray-800 rounded-bl-sm'
                }`}>
                  {m.msg_type !== 'general' && <div className="text-xs opacity-70 mb-1 uppercase">{m.msg_type}</div>}
                  <p>{m.content}</p>
                  <div className="text-xs opacity-60 mt-1">{new Date(m.created_at).toLocaleTimeString()}</div>
                </div>
              </div>
            ))}
          </div>
          <div className="border-t pt-3 space-y-2">
            <div className="flex gap-2">
              <Select value={msgType} onValueChange={setMsgType}>
                <SelectTrigger className="w-[120px] text-xs"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white"><SelectItem value="general">General</SelectItem><SelectItem value="class_link">Class Link</SelectItem><SelectItem value="timing">Timing</SelectItem><SelectItem value="payment">Payment</SelectItem></SelectContent>
              </Select>
              <Input value={msgContent} onChange={e => setMsgContent(e.target.value)} placeholder="Type a message..." className="flex-1 bg-gray-50 focus:border-[#0891B2]"
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMsg(); } }} data-testid="msg-input" />
              <button onClick={sendMsg} className="bb-btn bb-btn-primary !py-2 !px-4 !text-sm !shadow-[2px_2px_0px_rgba(15,23,42,0.9)]" data-testid="send-msg-btn"><i className="fa-solid fa-paper-plane" /></button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      {/* Add Enrollment Dialog */}
      <Dialog open={addEnrollmentDialog} onOpenChange={setAddEnrollmentDialog}>
        <DialogContent className="bg-white border-gray-200 max-w-md">
          <DialogHeader><DialogTitle style={{ fontFamily:'Outfit' }}>Add Enrollment</DialogTitle></DialogHeader>
          <form onSubmit={createEnrollment} className="space-y-3 mt-2">
            <div><label className="text-xs text-gray-500 mb-1 block">Student</label>
              <Select value={addEnrollmentForm.user_id} onValueChange={v => setAddEnrollmentForm(p=>({...p,user_id:v}))}>
                <SelectTrigger className="bg-gray-50"><SelectValue placeholder="Select student" /></SelectTrigger>
                <SelectContent className="bg-white">{users.map(u=><SelectItem key={u._id} value={u._id}>{u.name} ({u.email})</SelectItem>)}</SelectContent>
              </Select></div>
            <div><label className="text-xs text-gray-500 mb-1 block">Course</label>
              <Select value={addEnrollmentForm.course_id} onValueChange={v => setAddEnrollmentForm(p=>({...p,course_id:v}))}>
                <SelectTrigger className="bg-gray-50"><SelectValue placeholder="Select course" /></SelectTrigger>
                <SelectContent className="bg-white">{courses.map(c=><SelectItem key={c.id} value={c.id}>{c.title}</SelectItem>)}</SelectContent>
              </Select></div>
            <div><label className="text-xs text-gray-500 mb-1 block">Phone (optional)</label><Input value={addEnrollmentForm.phone} onChange={e=>setAddEnrollmentForm(p=>({...p,phone:e.target.value}))} className="bg-gray-50 focus:border-[#0891B2]" /></div>
            <div><label className="text-xs text-gray-500 mb-1 block">Message (optional)</label><Textarea value={addEnrollmentForm.message} onChange={e=>setAddEnrollmentForm(p=>({...p,message:e.target.value}))} rows={2} className="bg-gray-50 focus:border-[#0891B2] resize-none" /></div>
            <div className="flex gap-3 pt-2">
              <Button type="button" variant="outline" onClick={()=>setAddEnrollmentDialog(false)} className="flex-1">Cancel</Button>
              <button type="submit" className="bb-btn bb-btn-primary flex-1 !text-sm !py-2 !rounded-lg">Add</button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Student Dialog */}
      <Dialog open={addStudentDialog} onOpenChange={setAddStudentDialog}>
        <DialogContent className="bg-white border-gray-200 max-w-md">
          <DialogHeader><DialogTitle style={{ fontFamily:'Outfit' }}>Add Student</DialogTitle></DialogHeader>
          <form onSubmit={createStudent} className="space-y-3 mt-2">
            <div><label className="text-xs text-gray-500 mb-1 block">Name</label><Input value={addStudentForm.name} onChange={e=>setAddStudentForm(p=>({...p,name:e.target.value}))} className="bg-gray-50 focus:border-[#0891B2]" required /></div>
            <div><label className="text-xs text-gray-500 mb-1 block">Email</label><Input type="email" value={addStudentForm.email} onChange={e=>setAddStudentForm(p=>({...p,email:e.target.value}))} className="bg-gray-50 focus:border-[#0891B2]" required /></div>
            <div><label className="text-xs text-gray-500 mb-1 block">Phone</label><Input value={addStudentForm.phone} onChange={e=>setAddStudentForm(p=>({...p,phone:e.target.value}))} className="bg-gray-50 focus:border-[#0891B2]" /></div>
            <div><label className="text-xs text-gray-500 mb-1 block">Password</label><Input type="password" value={addStudentForm.password} onChange={e=>setAddStudentForm(p=>({...p,password:e.target.value}))} className="bg-gray-50 focus:border-[#0891B2]" required /></div>
            <div className="flex gap-3 pt-2">
              <Button type="button" variant="outline" onClick={()=>setAddStudentDialog(false)} className="flex-1">Cancel</Button>
              <button type="submit" className="bb-btn bb-btn-primary flex-1 !text-sm !py-2 !rounded-lg">Add</button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Inquiry Dialog */}
      <Dialog open={addInquiryDialog} onOpenChange={setAddInquiryDialog}>
        <DialogContent className="bg-white border-gray-200 max-w-md">
          <DialogHeader><DialogTitle style={{ fontFamily:'Outfit' }}>Add Inquiry</DialogTitle></DialogHeader>
          <form onSubmit={createInquiry} className="space-y-3 mt-2">
            <div><label className="text-xs text-gray-500 mb-1 block">Name</label><Input value={addInquiryForm.name} onChange={e=>setAddInquiryForm(p=>({...p,name:e.target.value}))} className="bg-gray-50 focus:border-[#0891B2]" required /></div>
            <div><label className="text-xs text-gray-500 mb-1 block">Email</label><Input type="email" value={addInquiryForm.email} onChange={e=>setAddInquiryForm(p=>({...p,email:e.target.value}))} className="bg-gray-50 focus:border-[#0891B2]" required /></div>
            <div><label className="text-xs text-gray-500 mb-1 block">Phone</label><Input value={addInquiryForm.phone} onChange={e=>setAddInquiryForm(p=>({...p,phone:e.target.value}))} className="bg-gray-50 focus:border-[#0891B2]" required /></div>
            <div><label className="text-xs text-gray-500 mb-1 block">Age (optional)</label><Input value={addInquiryForm.age} onChange={e=>setAddInquiryForm(p=>({...p,age:e.target.value}))} className="bg-gray-50 focus:border-[#0891B2]" /></div>
            <div><label className="text-xs text-gray-500 mb-1 block">Message</label><Textarea value={addInquiryForm.message} onChange={e=>setAddInquiryForm(p=>({...p,message:e.target.value}))} rows={3} className="bg-gray-50 focus:border-[#0891B2] resize-none" required /></div>
            <div className="flex gap-3 pt-2">
              <Button type="button" variant="outline" onClick={()=>setAddInquiryDialog(false)} className="flex-1">Cancel</Button>
              <button type="submit" className="bb-btn bb-btn-primary flex-1 !text-sm !py-2 !rounded-lg">Add</button>
            </div>
          </form>
        </DialogContent>
            </Dialog>
    </div>
  );
}